import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ListItem } from '@ispm-client/modules/widgets/models/list-item';
import { Subject } from 'rxjs/Subject';
import {
  ProcessUserSelectedElementAction, RestartExecutionAction,
  selectExecutionHistoryParsingStatus,
  selectExecutionPath
} from '@ispm-client/modules/process-executor/store';
import { BP_Status } from '@ispm-client/modules/process-executor/store/state';
import { select, Store } from '@ngrx/store';
import { selectExecutionPathAsListItems } from '@ispm-client/modules/process-executor/store/selectors';
import { ExecutionPath } from '@ispm-client/modules/process-executor';
import { concatMap, filter, map, mergeMap, take, takeUntil, toArray } from 'rxjs/operators';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { PathwaysConfigurationService } from '@ispm-client/modules/pathways/services';
import { CLINICAL_INFORMATION, PATHWAYS_SELECTION } from '../consts';
import { ConfigurationStoreHandler } from '../../../app/configuration/services/configuration-store-handler.service';
import { get } from 'lodash';
import { ClinicalInfoForm } from '..';
import { AgregationService } from '@ispm-client/modules/aggregator/services';

@Component({
  selector:    'ispm-pathways-panel',
  templateUrl: './pathways-panel.component.html',
  styleUrls:   ['./pathways-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PathwaysPanelComponent implements OnInit, OnDestroy {

  executionPath: ExecutionPath;
  resetDisabled = true;
  selectActive = false;

  private destroyed$: Subject<any> = new Subject();
  public forms$: Observable<ClinicalInfoForm[]>;
  private pathwaySelectionEnabled = false;
  private hideResetButton = false;

  public tabs = [PATHWAYS_SELECTION, CLINICAL_INFORMATION];
  public selectedTab = PATHWAYS_SELECTION;
  public formIds;

  get resetButtonDisplayed(): boolean {
      return !this.hideResetButton && this.pathwaySelectionEnabled;
  }
  public items$: BehaviorSubject<Array<ListItem>> = new BehaviorSubject<Array<ListItem>>([]);
  public selectableItem$: BehaviorSubject<ListItem> = new BehaviorSubject<ListItem>(null);

  constructor(private store$: Store<any>,
              private pathwayConfService: PathwaysConfigurationService,
              private _configurationHandler: ConfigurationStoreHandler,
              public queryAgregator: AgregationService) { }

  ngOnInit() {
    this.subscribeToExecutionPath();
    this.subscribeToBpStatus();
    this.subscribeToPathwaySelectionEnabled();
    this.subscribeToResetButtonHideConfig();
    this.forms$ = this._configurationHandler.getCurrentConfig()
    .pipe(
      take(1),
      map(configuration => get(configuration, 'ClinicalInformationLayout.forms', [])),
      mergeMap(forms => forms.map(form => new ClinicalInfoForm(form.formId, form.formName, form.attributes, this.queryAgregator))),
      concatMap((form: ClinicalInfoForm) => forkJoin([of(form), this.isFormHidden(form).pipe(take(1))])),
      filter(([form, isHidden]) => !isHidden),
      map(([form, isHidden]) => form),
      toArray()
    );
  }

   isFormHidden(form: ClinicalInfoForm): Observable<boolean> {
    return form.needToHideForm();
  }

  updateSelectActive() {
    this.selectActive = !(this.executionPath === null || this.executionPath === undefined) &&
      !(this.executionPath.currentUserTaskHolder === null || this.executionPath.currentUserTaskHolder === undefined) &&
      this.pathwaySelectionEnabled;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSelect($event) {
    this.store$.dispatch(new ProcessUserSelectedElementAction($event));
  }

  reset() {
    this.store$.dispatch(new RestartExecutionAction());
  }

  private subscribeToPathwaySelectionEnabled() {
    this.pathwayConfService.isPathwaySelectionEnabled()
      .takeUntil(this.destroyed$.asObservable())
      .subscribe(enabled => {
        this.pathwaySelectionEnabled = enabled;
        this.updateSelectActive();
      });
  }

  private subscribeToExecutionPath() {
    this.store$.pipe(
      select(selectExecutionPath),
      takeUntil(this.destroyed$.asObservable())
    ).subscribe(executedPath => {
      this.executionPath = executedPath;
      this.updateSelectActive();
    });
  }

  private subscribeToBpStatus() {
    this.store$.pipe(
      select(selectExecutionHistoryParsingStatus),
      takeUntil(this.destroyed$.asObservable())
    ).subscribe(status => {
        if (status === BP_Status.Completed) {
          this.resetDisabled = !(this.executionPath.executedNodes && this.executionPath.executedNodes.length > 0);
          this.items$.next(selectExecutionPathAsListItems.projector(this.executionPath).filter(node => !node.enable));
          this.selectableItem$.next(selectExecutionPathAsListItems.projector(this.executionPath).find(node => node.enable));
        }
      }
    );
  }

  private subscribeToResetButtonHideConfig() {
    this.pathwayConfService.setUpHideResetButtonValue()
      .pipe(takeUntil(this.destroyed$.asObservable()))
      .subscribe((ifHide) => {
        this.hideResetButton = ifHide;
      });
  }

  public onTabChanged(tabName: string) {
    this.selectedTab = tabName;
  }

  public isSelected(tabName: string) {
    return this.selectedTab === tabName;
  }
}
